import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, ArrowRight, Check, Globe, User, Clock, Plus, Zap } from 'lucide-react'
import { api } from '../lib/api'
import { validateUrl, validateCronExpression } from '../lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'

// Form schemas
const urlSchema = z.object({
  name: z.string().min(1, 'Test name is required'),
  baseUrl: z.string().min(1, 'URL is required').refine(validateUrl, 'Please enter a valid URL'),
})

const personaSchema = z.object({
  personaId: z.string().optional(),
})

const scheduleSchema = z.object({
  scheduleType: z.enum(['manual', 'interval', 'cron']),
  intervalMinutes: z.number().min(1).optional(),
  cronExpression: z.string().optional(),
  timezone: z.string().optional(),
}).refine((data) => {
  if (data.scheduleType === 'interval') {
    return data.intervalMinutes && data.intervalMinutes > 0
  }
  if (data.scheduleType === 'cron') {
    return data.cronExpression && validateCronExpression(data.cronExpression)
  }
  return true
}, {
  message: 'Please provide valid schedule configuration',
  path: ['scheduleType']
})

type UrlFormData = z.infer<typeof urlSchema>
type PersonaFormData = z.infer<typeof personaSchema>
type ScheduleFormData = z.infer<typeof scheduleSchema>

interface TestData extends UrlFormData, PersonaFormData, ScheduleFormData {}

export function TestWizard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [currentStep, setCurrentStep] = useState(1)
  const [testData, setTestData] = useState<Partial<TestData>>({})

  // Fetch personas for step 2
  const { data: personasData, isLoading: isLoadingPersonas } = useQuery({
    queryKey: ['personas'],
    queryFn: () => api.getPersonas(),
  })

  // Create test mutation
  const createTestMutation = useMutation({
    mutationFn: (data: TestData) => {
      const payload = {
        name: data.name,
        base_url: data.baseUrl,
        persona_id: data.personaId && data.personaId !== 'none' ? parseInt(data.personaId) : undefined,
        schedule: data.scheduleType !== 'manual' ? {
          cron: data.scheduleType === 'cron' 
            ? data.cronExpression!
            : `*/${data.intervalMinutes} * * * *`, // Convert interval to cron
          timezone: data.timezone || 'UTC',
        } : undefined,
      }
      return api.createTest(payload)
    },
    onSuccess: (test) => {
      queryClient.invalidateQueries({ queryKey: ['tests'] })
      navigate(`/tests/${test.id}`)
    },
  })

  // Step 1: URL Configuration
  const urlForm = useForm<UrlFormData>({
    resolver: zodResolver(urlSchema),
    defaultValues: {
      name: testData.name || '',
      baseUrl: testData.baseUrl || '',
    },
  })

  // Step 2: Persona Selection
  const personaForm = useForm<PersonaFormData>({
    resolver: zodResolver(personaSchema),
    defaultValues: {
      personaId: testData.personaId || 'none',
    },
  })

  // Step 3: Schedule Configuration
  const scheduleForm = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      scheduleType: testData.scheduleType || 'manual',
      intervalMinutes: testData.intervalMinutes || 60,
      cronExpression: testData.cronExpression || '0 9 * * *',
      timezone: testData.timezone || 'UTC',
    },
  })

  const onUrlSubmit = (data: UrlFormData) => {
    setTestData(prev => ({ ...prev, ...data }))
    setCurrentStep(2)
  }

  const onPersonaSubmit = (data: PersonaFormData) => {
    setTestData(prev => ({ ...prev, ...data }))
    setCurrentStep(3)
  }

  const onScheduleSubmit = (data: ScheduleFormData) => {
    const finalData = { ...testData, ...data } as TestData
    createTestMutation.mutate(finalData)
  }

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const steps = [
    { number: 1, title: 'URL Configuration', icon: Globe },
    { number: 2, title: 'AI Persona', icon: User },
    { number: 3, title: 'Schedule', icon: Clock },
  ]

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/tests')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tests
          </Button>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-3xl font-bold">Create New Test</h1>
        </div>
        <p className="text-muted-foreground">
          Set up automated browser testing with AI-powered interactions
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => {
          const Icon = step.icon
          const isActive = currentStep === step.number
          const isCompleted = currentStep > step.number
          
          return (
            <div key={step.number} className="flex items-center">
              <div className="flex flex-col items-center">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center border-2 transition-colors
                  ${isCompleted 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : isActive 
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-background border-muted-foreground/30 text-muted-foreground'
                  }
                `}>
                  {isCompleted ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>
                <div className="mt-2 text-center">
                  <div className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {step.title}
                  </div>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`
                  flex-1 h-0.5 mx-4 transition-colors
                  ${currentStep > step.number ? 'bg-green-500' : 'bg-muted-foreground/30'}
                `} />
              )}
            </div>
          )
        })}
      </div>

      {/* Step Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {React.createElement(steps[currentStep - 1].icon, { className: "h-5 w-5" })}
            {steps[currentStep - 1].title}
          </CardTitle>
          <CardDescription>
            {currentStep === 1 && "Configure the website URL and test details"}
            {currentStep === 2 && "Choose an AI persona to guide the testing behavior"}
            {currentStep === 3 && "Set up when and how often to run this test"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Step 1: URL Configuration */}
          {currentStep === 1 && (
            <form onSubmit={urlForm.handleSubmit(onUrlSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Test Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Homepage Login Flow"
                  {...urlForm.register('name')}
                />
                {urlForm.formState.errors.name && (
                  <p className="text-sm text-destructive">{urlForm.formState.errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="baseUrl">Website URL</Label>
                <Input
                  id="baseUrl"
                  placeholder="https://example.com"
                  {...urlForm.register('baseUrl')}
                />
                {urlForm.formState.errors.baseUrl && (
                  <p className="text-sm text-destructive">{urlForm.formState.errors.baseUrl.message}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  The base URL where your test will start
                </p>
              </div>

              <div className="flex justify-end">
                <Button type="submit">
                  Next: Choose Persona
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </form>
          )}

          {/* Step 2: Persona Selection */}
          {currentStep === 2 && (
            <form onSubmit={personaForm.handleSubmit(onPersonaSubmit)} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>AI Persona</Label>
                  <Select
                    value={personaForm.watch('personaId')}
                    onValueChange={(value) => personaForm.setValue('personaId', value)}
                    disabled={isLoadingPersonas}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={isLoadingPersonas ? "Loading personas..." : "Select a persona or leave blank for default behavior"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Default (No specific persona)</SelectItem>
                      {personasData?.personas.map((persona) => (
                        <SelectItem key={persona.id} value={persona.id.toString()}>
                          {persona.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Personas define how the AI behaves during testing
                  </p>
                </div>

                {/* Show selected persona details */}
                {personaForm.watch('personaId') && personaForm.watch('personaId') !== 'none' && (
                  <div className="p-4 bg-muted rounded-lg">
                    {(() => {
                      const selectedPersona = personasData?.personas.find(
                        p => p.id.toString() === personaForm.watch('personaId')
                      )
                      return selectedPersona ? (
                        <div>
                          <h4 className="font-medium mb-2">{selectedPersona.name}</h4>
                          <p className="text-sm text-muted-foreground">{selectedPersona.prompt}</p>
                        </div>
                      ) : null
                    })()}
                  </div>
                )}

                {/* Create new persona link */}
                <div className="text-center py-4">
                  <Button variant="outline" asChild>
                    <a href="/personas" target="_blank" rel="noopener noreferrer">
                      <Plus className="h-4 w-4 mr-2" />
                      Create New Persona
                    </a>
                  </Button>
                </div>
              </div>

              <div className="flex justify-between">
                <Button type="button" variant="outline" onClick={goBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button type="submit">
                  Next: Set Schedule
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </form>
          )}

          {/* Step 3: Schedule Configuration */}
          {currentStep === 3 && (
            <form onSubmit={scheduleForm.handleSubmit(onScheduleSubmit)} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Schedule Type</Label>
                  <Select
                    value={scheduleForm.watch('scheduleType')}
                    onValueChange={(value: 'manual' | 'interval' | 'cron') =>
                      scheduleForm.setValue('scheduleType', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual (Run on demand)</SelectItem>
                      <SelectItem value="interval">Interval (Every X minutes)</SelectItem>
                      <SelectItem value="cron">Cron Expression (Advanced)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Interval Configuration */}
                {scheduleForm.watch('scheduleType') === 'interval' && (
                  <div className="space-y-2">
                    <Label htmlFor="intervalMinutes">Interval (minutes)</Label>
                    <Input
                      id="intervalMinutes"
                      type="number"
                      min="1"
                      placeholder="60"
                      {...scheduleForm.register('intervalMinutes', { valueAsNumber: true })}
                    />
                    <p className="text-sm text-muted-foreground">
                      Test will run every {scheduleForm.watch('intervalMinutes') || 60} minutes
                    </p>
                  </div>
                )}

                {/* Cron Configuration */}
                {scheduleForm.watch('scheduleType') === 'cron' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="cronExpression">Cron Expression</Label>
                      <Input
                        id="cronExpression"
                        placeholder="0 9 * * *"
                        {...scheduleForm.register('cronExpression')}
                      />
                      {scheduleForm.formState.errors.cronExpression && (
                        <p className="text-sm text-destructive">
                          {scheduleForm.formState.errors.cronExpression.message}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        Example: "0 9 * * *" runs daily at 9:00 AM
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="timezone">Timezone</Label>
                      <Select
                        value={scheduleForm.watch('timezone')}
                        onValueChange={(value) => scheduleForm.setValue('timezone', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UTC">UTC</SelectItem>
                          <SelectItem value="America/New_York">Eastern Time</SelectItem>
                          <SelectItem value="America/Chicago">Central Time</SelectItem>
                          <SelectItem value="America/Denver">Mountain Time</SelectItem>
                          <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                          <SelectItem value="Europe/London">London</SelectItem>
                          <SelectItem value="Europe/Paris">Paris</SelectItem>
                          <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Schedule Summary */}
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">Schedule Summary</h4>
                  <div className="text-sm text-muted-foreground">
                    {scheduleForm.watch('scheduleType') === 'manual' && (
                      <p>This test will only run when manually triggered.</p>
                    )}
                    {scheduleForm.watch('scheduleType') === 'interval' && (
                      <p>
                        This test will run automatically every{' '}
                        <Badge variant="secondary">{scheduleForm.watch('intervalMinutes') || 60} minutes</Badge>
                      </p>
                    )}
                    {scheduleForm.watch('scheduleType') === 'cron' && (
                      <p>
                        This test will run on schedule:{' '}
                        <Badge variant="secondary">{scheduleForm.watch('cronExpression') || '0 9 * * *'}</Badge>
                        {' '}in {scheduleForm.watch('timezone') || 'UTC'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <Button type="button" variant="outline" onClick={goBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={createTestMutation.isPending}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                >
                  {createTestMutation.isPending ? 'Creating...' : 'Create Test'}
                  <Check className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
